import { useTranslations } from "../../contexts/Translation";
import { H2, H3 } from "../Headers/Headings";
import Section from "../Section";
import { useState } from "react";
import Button from "../Button/Button";
import ImageUploader from "./ImageUpload/ImageUploader";
import { useApiData } from "../../contexts/ApiData";
import { Profile, SettingsFormProps, ValidationError } from "../../types";
import ErrorText from "./ErrorText";
import Input from "./Input";
import { useAuth } from "../../lib/auth";
import Link from "../Link/Link";
import ProfileImage from "../ProfileImage";
import { useNavigate } from "react-router-dom";
import { useRefreshUser } from "../../contexts/UserProvider";
import Card from "../Card/Card";
import styles from "./FormStyles.module.css";

type ProfileFormProps = SettingsFormProps & {
  profile?: Profile;
};

const ProfileForm = ({ isEdit = false, profile }: ProfileFormProps) => {
  const t = useTranslations("createProfile");
  const { user } = useAuth();
  const navigate = useNavigate();
  const refreshUser = useRefreshUser();
  const [image, setImage] = useState<string>("");
  const [showUploader, setShowUploader] = useState(false);
  const [candidate, setCandidate] = useState<string>(profile?.candidate || "");
  const [title, setTitle] = useState<string>(profile?.title || "");
  const [consentToTerms, setConsentToTerms] = useState<number>(profile?.consentToTerms || 0);
  const { post, put } = useApiData();
  const [errors, setErrors] = useState<ValidationError[]>([]);

  const saveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    const apiFn = isEdit ? put : post;
    if (!user || !user.email || user.email.length < 5) {
      setErrors([...errors, { name: "form", message: t("emailMissing") }]);
    } else if (candidate.length < 2) {
      setErrors([...errors, { name: "candidate", message: t("nameMissing") }]);
    } else if (!consentToTerms) {
      setErrors([...errors, { name: "consentToTerms", message: t("consentToTermsRequired") }]);
    } else {
      const result = await apiFn(`/profile`, { image, candidate, title, email: user.email, consentToTerms });
      if (!result) {
        setErrors([{ name: "form", message: t("serverError") }]);
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      } else {
        await refreshUser();
        if (isEdit) {
        } else {
          // For new profile creation, redirect to dashboard with welcome parameter
          navigate("/?welcome=true");
        }
      }
    }
  };

  const updateImage = (dataUrl: string) => {
    setImage(dataUrl);
  };

  return (
    <Section>
      <H2>{t(isEdit ? "editHeading" : "heading")} </H2>

      <form className={styles.form} onSubmit={saveProfile}>
        <ErrorText error={errors.find((e) => e.name === "form")} />
        <Card className={styles.cardSection} padding='large' variant='fixed'>
          <ProfileImage src={profile?.image ? `/files/profile/${profile.image}` : ""} alt={t("profileImageAlt")} variant='xl' />
          <div className={styles.fieldRow}>
            <Input
              name='candidate'
              error={errors.find((e) => e.name === "candidate")}
              handleChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setErrors([]);
                setCandidate(e.target.value);
              }}
              label={t("candidate")}
              helpText={t("help.candidate")}
              value={candidate}
            />
            <Input
              name='title'
              error={errors.find((e) => e.name === "title")}
              handleChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setErrors([]);
                setTitle(e.target.value);
              }}
              label={t("title")}
              helpText={t("help.title")}
              value={title}
            />
          </div>
        </Card>
        <H3>{t("profileImageLabel")}</H3>
        <Card className={styles.cardSection} padding='large' variant='fixed'>
          <div className={styles.mediaRow}>
            <ProfileImage src={image || (profile?.image ? `/files/profile/${profile.image}` : "")} alt={t("profileImageAlt")} variant='large' />
            <Button type='button' onClick={() => setShowUploader(true)}>
              {t("uploadImage")}
            </Button>
          </div>
          {showUploader && (
            <ImageUploader
              maxWidth={512}
              maxHeight={512}
              aspectRatios={["square"]}
              defaultAspect='square'
              original={image || undefined}
              onImageReady={(base64) => {
                updateImage(base64);
                setShowUploader(false);
              }}
              onClose={() => setShowUploader(false)}
            />
          )}
        </Card>
        <Card className={styles.cardSection} padding='large' variant='fixed'>
          <Input
            type='checkbox'
            error={errors.find((e) => e.name === "consentToTerms")}
            name='consentToTerms'
            handleChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setErrors([]);
              setConsentToTerms(e.target.checked ? 1 : 0);
            }}
            label={t("consentToTerms")}
            value={consentToTerms}
            infoText={
              <>
                <Link target='_blank' rel='noreferrer' href='https://feedbackjourney.com/agreement'>
                  {t("termsLink")}
                </Link>
              </>
            }
          />
        </Card>

        <Button>{t(isEdit ? "editHeading" : "saveBtn")}</Button>
      </form>
    </Section>
  );
};

export default ProfileForm;
